import { Chip, styled } from '@mui/material';
import { Table } from '@tanstack/react-table';
import { L } from 'harmony-language';
import React from 'react';
import { QueryKeys } from '../../../api/config';
import { useAgisticsContext } from '../../../app/agistics-context';
import { Load } from '../../../types';
import { getWorkingWeek } from '../../../utils/date-time-utils';
import { CircularLoading } from '../../shared/circular-loading';
import { useUpdateUserPreferences } from '../../shared/hooks/use-update-user-preferences';
import { AgisticsTablePaging } from '../../shared/table/components/agistics-table-paging';
import { useDraftsLoadsContext } from '../drafts-loads-context';
import { GlobalFilter, GlobalFilterItem } from '../hooks/use-draft-load-table';
import { getActiveDrawerFilters, globalFilterRemover, removeDrawerFilters, showSelectedOnly } from '../utils/drafts-loads-utils';
import { LoadTableBody } from './load-table-body';
import { LoadTableHeader } from './load-table-header';

interface DraftLoadTableProps {
    isLoading: boolean;
    isFetching: boolean;
    table: Table<Load>;
}

export const DraftLoadTable: React.FC<DraftLoadTableProps> = (props) => {
    const { isLoading, isFetching, table } = props;
    const { isDrafts } = useDraftsLoadsContext();
    const { preferences, updatePreferences } = useUpdateUserPreferences();

    const setPageSizePreferences = (pageSize: number) => {
        const preferencesKey = isDrafts ? 'drafts' : 'loads';

        const newPreferences = {
            ...preferences,
            tables: {
                ...preferences.tables,
                [preferencesKey]: {
                    pageSize: pageSize,
                }
            }
        }

        updatePreferences(newPreferences);
    }

    return (isLoading || isFetching) ? <CircularLoading /> : (
        <>
            <AgisticsTablePaging table={table} setPageSizePreferences={setPageSizePreferences}>
                <DraftsLoadsTableButtons table={table} />
            </AgisticsTablePaging>
            <div className='lt-base'>
                <LoadTableHeader table={table} />
                <LoadTableBody table={table} />
            </div>
        </>
    );
};

interface DraftsLoadsTableButtonsProps {
    table: Table<Load>;
}
const DraftsLoadsTableButtons = (props: DraftsLoadsTableButtonsProps) => {
    const { table } = props;
    const { dateFilters, updateDateFilters } = useAgisticsContext();
    const { isDrafts } = useDraftsLoadsContext();
    const apiKey = isDrafts ? QueryKeys.drafts : QueryKeys.loads;
    const week0 = getWorkingWeek(0);
    const dateFilter = isDrafts ? dateFilters[QueryKeys.drafts] : dateFilters[QueryKeys.loads];
    const hasOrderGroupFilter = Boolean(dateFilter.orderGroupGuid);
    const numSelectedRows = table.getSelectedRowModel().rows.length;
    const isDisplayOnlyActive = table.getState().globalFilter?.filter((filterItem: GlobalFilterItem) => filterItem.type === 'selectedFilter').length > 0;
    const activeDrawerFilters = getActiveDrawerFilters(table);
    const hasActiveDrawerFilters = activeDrawerFilters && activeDrawerFilters.length > 0;
    
    async function removeOrderGroupFilter() {
        updateDateFilters(apiKey, {
            page: isDrafts ? '/drafts' : '/loads',
            mode: 'week',
            from: week0.sundayIso,
            to: week0.saturdayIso,
            includeDrafts: !!isDrafts,
            offset: 0,
            orderGroupGuid: undefined,
        });
    }

    function toggleSelectionDisplay() {
        if (!isDisplayOnlyActive) {
            showSelectedOnly(table);
        } else {
            globalFilterRemover(table, 'selectedFilter');
        }
    }

    return (
        <>
            {hasOrderGroupFilter ? (
                <DisplayModeChip label={L.orderGroup(dateFilter.orderGroupGuid!)} onDelete={removeOrderGroupFilter} />
            ) : (
                numSelectedRows > 0 && (
                    <>
                        &nbsp;-&nbsp;{L.xSelected(numSelectedRows.toString())}
                        <DisplayModeChip
                            label={isDisplayOnlyActive ? L.showAll() : L.showSelectedOnly()}
                            variant={isDisplayOnlyActive ? 'outlined' : 'filled'}
                            onClick={toggleSelectionDisplay}
                        />
                    </>
                )
            )}
            {hasActiveDrawerFilters && (
                <>
                    <FilterChip
                        label={L.clearFilters()}
                        onClick={() => removeDrawerFilters(table)}
                    />
                    {activeDrawerFilters.map((filterItem: GlobalFilterItem) => (
                        <FilterChip
                            key={`${filterItem.type}-${String(filterItem.value)}`}
                            // @ts-ignore - barking at 'label' not existing for GlobalFilterItems that have been filtered out
                            label={filterItem.label}
                            onDelete={() => {
                                table.setGlobalFilter((currentGlobalFilter: GlobalFilter) => {
                                    const newFilter = currentGlobalFilter?.filter(
                                        // We could match on every value to guarantee 'this' instance is removed, but this should be sufficient
                                        (item) => item.type !== filterItem.type && item.label !== filterItem.label
                                    );
                                    return newFilter?.length ? newFilter : undefined;
                                });
                            }}
                        />
                    ))}
                </>
            )}
        </>
    );
}

const DisplayModeChip = styled(Chip)({
    margin: '0 0.5rem',
});

const FilterChip = styled(Chip)({
    margin: '0 0.25rem',
});